/* color-combinations.js - filter, search, row-panel, add-to-cart */
(function () {
  'use strict';

  const root = document.querySelector('[data-color-combos]');
  if (!root) return;

  // ── Storefront API pairing images (KFO only) ──────────────────────────────

  function pairingKey(a, b) {
    const la = a.toLowerCase().trim(), lb = b.toLowerCase().trim();
    return la < lb ? la + '~~~' + lb : lb + '~~~' + la;
  }

  function numericId(gid) {
    return gid ? String(gid).split('/').pop() : '';
  }

  // Relative URL keeps the current language subdomain (locale-safe), unlike the
  // absolute onlineStoreUrl.
  function variantUrl(v) {
    const handle = v && v.product && v.product.handle;
    const id = numericId(v && v.id);
    return handle ? `/products/${handle}?variant=${id}` : '#';
  }

  async function fetchAllPairings(shop, token) {
    const endpoint = `https://${shop}/api/2024-10/graphql.json`;
    const query = `query GetPairings($cursor: String) {
      metaobjects(type: "kfo_color_pairing", first: 250, after: $cursor) {
        pageInfo { hasNextPage endCursor }
        nodes { fields {
          key
          value
          reference {
            ... on ProductVariant {
              id
              title
              availableForSale
              image { url(transform: { maxWidth: 600 }) }
              product { title handle }
            }
            ... on MediaImage { image { url(transform: { maxWidth: 600 }) } }
          }
        } }
      }
    }`;
    const lookup = {};
    const contrastPairs = [];
    let cursor = null, hasMore = true;
    while (hasMore) {
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Shopify-Storefront-Access-Token': token },
        body: JSON.stringify({ query, variables: { cursor } })
      });
      const data = await res.json();
      const page = data?.data?.metaobjects;
      if (!page) break;
      page.nodes.forEach(node => {
        const fields = {};
        node.fields.forEach(f => { fields[f.key] = f; });
        const a      = fields.yarn_a?.reference;
        const b      = fields.yarn_b?.reference;
        const imgUrl = fields.pairing_image?.reference?.image?.url;
        if (!(a?.title && b?.title && imgUrl)) return;

        // Harmonic image lookup (unchanged): contrast pairs aren't referenced by
        // any base's matches, so populating them here is harmless.
        lookup[pairingKey(a.title, b.title)] = imgUrl;

        const ptype = (fields.pairing_type?.value || '').toLowerCase().trim();
        if (ptype === 'contrast') {
          contrastPairs.push({
            aTitle: a.title, bTitle: b.title,
            aProduct: a.product?.title || '', bProduct: b.product?.title || '',
            aUrl: variantUrl(a), bUrl: variantUrl(b),
            aId: numericId(a.id), bId: numericId(b.id),
            aAvail: !!a.availableForSale, bAvail: !!b.availableForSale,
            img: imgUrl
          });
        }
      });
      hasMore = page.pageInfo.hasNextPage;
      cursor  = page.pageInfo.endCursor;
    }
    // Stable order: alphabetical by yarn_a, then yarn_b.
    contrastPairs.sort((x, y) =>
      x.aTitle.localeCompare(y.aTitle) || x.bTitle.localeCompare(y.bTitle));
    return { lookup, contrastPairs };
  }

  let pairingLookup = null, contrastPairs = [], pairingReady = null;

  const pairingConfig = document.getElementById('kfo-pairing-config');
  if (pairingConfig) {
    try {
      const cfg = JSON.parse(pairingConfig.textContent);
      if (cfg.shop && cfg.token) {
        pairingReady = fetchAllPairings(cfg.shop, cfg.token)
          .then(r => { pairingLookup = r.lookup; contrastPairs = r.contrastPairs; })
          .catch(() => { pairingLookup = {}; contrastPairs = []; });
      }
    } catch (_) { /* malformed JSON - skip */ }
  }

  async function applyPairingImages(panel, baseName) {
    if (!pairingReady) return;
    await pairingReady;
    panel.querySelectorAll('[data-match-name]').forEach(matchEl => {
      const key = pairingKey(baseName, matchEl.dataset.matchName);
      const imgUrl = pairingLookup[key];
      if (!imgUrl) return;
      const img = matchEl.querySelector('.combo-match__media img');
      if (img) img.src = imgUrl;
    });
  }

  const grid        = root.querySelector('[data-combos-grid]');
  const searchInput = root.querySelector('[data-combos-search]');
  const tabs        = Array.from(root.querySelectorAll('.color-combos__tab'));
  const noResults   = root.querySelector('[data-combos-no-results]');

  // Contrast mode (content-mode switch, independent from the yarn-group tabs).
  const modeBtns         = Array.from(root.querySelectorAll('[data-mode-btn]'));
  const yarnTabs         = root.querySelector('[data-yarn-tabs]');
  const contrastWrap     = root.querySelector('[data-contrast-mode]');
  const contrastGrid     = root.querySelector('[data-contrast-grid]');
  const contrastLoading  = root.querySelector('[data-contrast-loading]');
  const contrastEmpty    = root.querySelector('[data-contrast-empty]');
  const contrastNoRes    = root.querySelector('[data-contrast-no-results]');

  let activeTab   = tabs.length ? (tabs[0].dataset.tab || '') : '';
  let searchTerm  = '';
  let mode        = 'harmonic';
  let contrastRendered = false;

  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  function allCards() {
    return Array.from(grid.querySelectorAll('[data-combo-card]'));
  }

  // ── Panel ─────────────────────────────────────────────────────────────────

  function getLastCardInRow(card) {
    const targetTop = card.getBoundingClientRect().top;
    const visible   = allCards().filter(c => !c.hidden);
    let last = card;
    visible.forEach(c => {
      if (Math.abs(c.getBoundingClientRect().top - targetTop) < 5) last = c;
    });
    return last;
  }

  function removePanel() {
    const panel = grid.querySelector('[data-combo-panel]');
    if (panel) panel.remove();
  }

  function closeAll() {
    removePanel();
    allCards().forEach(c => {
      c.removeAttribute('data-open');
      c.querySelector('[data-combo-trigger]')?.setAttribute('aria-expanded', 'false');
    });
  }

  function openCard(card) {
    const body = card.querySelector('[data-combo-body]');
    if (!body) return;

    const alreadyOpen = card.hasAttribute('data-open');
    closeAll();
    if (alreadyOpen) return; // second click closes

    const lastInRow = getLastCardInRow(card);

    const panel = document.createElement('div');
    panel.className = 'combo-expand-panel';
    panel.dataset.comboPanel = '';

    // Product link at top
    const srcLink = body.querySelector('.combo-card__product-link');
    if (srcLink) {
      const hdr = document.createElement('div');
      hdr.className = 'combo-expand-panel__hdr';
      hdr.appendChild(srcLink.cloneNode(true));
      panel.appendChild(hdr);
    }

    // Horizontal scroll of match items
    const srcMatches = body.querySelector('.combo-card__matches');
    if (srcMatches) {
      const scroll = document.createElement('div');
      scroll.className = 'combo-expand-panel__scroll';
      scroll.innerHTML = srcMatches.innerHTML;
      panel.appendChild(scroll);
    }

    lastInRow.after(panel);
    card.setAttribute('data-open', '');
    card.querySelector('[data-combo-trigger]')?.setAttribute('aria-expanded', 'true');

    const baseName = card.dataset.colorName || '';
    applyPairingImages(panel, baseName);

    requestAnimationFrame(() => {
      panel.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    });
  }

  // ── Lightbox ──────────────────────────────────────────────────────────────
  // The shared image lightbox lives in assets/combo-lightbox.js, exposed as
  // window.BfComboLightbox — see that file for open/close/contains. Reused by
  // the PDP "Farbkombinationen" tab too.

  // ── Events ────────────────────────────────────────────────────────────────

  grid.addEventListener('click', e => {
    const trigger = e.target.closest('[data-combo-trigger]');
    if (trigger) {
      openCard(trigger.closest('[data-combo-card]'));
      return;
    }
    const atcBtn = e.target.closest('[data-combo-atc]');
    if (atcBtn) { addToCart(atcBtn); return; }

    // Lightbox: click on match media inside the expanded panel
    const media = e.target.closest('.combo-expand-panel .combo-match__media');
    if (media) {
      const img = media.querySelector('img');
      if (!img) return;
      const matchEl  = media.closest('[data-match-name]');
      const matchName = matchEl?.dataset.matchName || '';
      const openCard  = grid.querySelector('[data-combo-card][data-open]');
      const baseName  = openCard?.dataset.colorName || '';
      const caption   = baseName && matchName ? `${baseName} · ${matchName}` : (matchName || baseName);
      if (window.BfComboLightbox) window.BfComboLightbox.open(img.src, caption);
    }
  });

  // Close on outside click - but ignore clicks inside the lightbox,
  // which lives at <body> level and would otherwise collapse the row panel
  // every time the user opens or closes a zoomed pairing image.
  document.addEventListener('click', e => {
    if (window.BfComboLightbox && window.BfComboLightbox.contains(e.target)) return;
    if (!grid.contains(e.target)) closeAll();
  });

  // ── Filter ────────────────────────────────────────────────────────────────

  function applyFilters() {
    closeAll();
    let visible = 0;
    allCards().forEach(card => {
      const tabMatch    = !activeTab   || (card.dataset.yarnGroup || '') === activeTab;
      const searchMatch = !searchTerm  || (card.dataset.colorName || '').includes(searchTerm);
      card.hidden = !(tabMatch && searchMatch);
      if (!card.hidden) visible++;
    });
    if (noResults) noResults.hidden = visible > 0;
  }

  tabs.forEach(tab => {
    tab.addEventListener('click', () => {
      tabs.forEach(t => { t.classList.remove('is-active'); t.setAttribute('aria-selected', 'false'); });
      tab.classList.add('is-active');
      tab.setAttribute('aria-selected', 'true');
      activeTab = tab.dataset.tab || '';
      applyFilters();
    });
  });

  if (searchInput) {
    searchInput.addEventListener('input', () => {
      searchTerm = searchInput.value.trim().toLowerCase();
      if (mode === 'contrast') applyContrastSearch(); else applyFilters();
    });
  }

  // ── Contrast mode ───────────────────────────────────────────────────────────

  function buildContrastTile(p) {
    const caption = `${esc(p.aTitle)} × ${esc(p.bTitle)}`;
    return (
      `<article class="contrast-pair" data-contrast-pair` +
        ` data-a="${esc(p.aTitle.toLowerCase())}" data-b="${esc(p.bTitle.toLowerCase())}">` +
        `<button class="contrast-pair__media" type="button" data-contrast-media` +
          ` data-caption="${caption}" aria-label="${caption}">` +
          `<img src="${esc(p.img)}" alt="${caption}" loading="lazy" width="600" height="600">` +
        `</button>` +
        `<p class="contrast-pair__title">` +
          `<strong>${esc(p.aTitle)}</strong>` +
          `<span class="contrast-pair__x" aria-hidden="true">×</span>` +
          `<em>${esc(p.bTitle)}</em>` +
        `</p>` +
        `<div class="contrast-pair__yarns">` +
          contrastYarnRow(p.aProduct, p.aTitle, p.aUrl, p.aId, p.aAvail) +
          contrastYarnRow(p.bProduct, p.bTitle, p.bUrl, p.bId, p.bAvail) +
        `</div>` +
      `</article>`
    );
  }

  function contrastYarnRow(product, variant, url, id, available) {
    const label = product ? `${esc(product)} · ${esc(variant)}` : esc(variant);
    const action = (available && id)
      ? `<button class="contrast-pair__atc btn btn--secondary" type="button" data-combo-atc="${esc(id)}">`
          + `${esc(bfT('add_to_cart', 'In den Warenkorb'))}</button>`
      : `<span class="contrast-pair__oos t-mono">${esc(bfT('sold_out', 'Ausverkauft'))}</span>`;
    return (
      `<div class="contrast-pair__yarn">` +
        `<a class="contrast-pair__link t-mono" href="${esc(url)}">${label} →</a>` +
        action +
      `</div>`
    );
  }

  function renderContrast() {
    if (!contrastGrid) return;
    if (contrastRendered) { applyContrastSearch(); return; }

    const done = () => {
      contrastRendered = true;
      if (contrastLoading) contrastLoading.hidden = true;
      if (contrastPairs.length) {
        contrastGrid.insertAdjacentHTML('beforeend', contrastPairs.map(buildContrastTile).join(''));
      }
      applyContrastSearch();
    };

    if (contrastLoading) contrastLoading.hidden = false;
    if (contrastEmpty) contrastEmpty.hidden = true;
    if (pairingReady) pairingReady.then(done).catch(done);
    else done(); // no token / no fetch → resolves to empty state
  }

  function applyContrastSearch() {
    if (!contrastGrid) return;
    const tiles = contrastGrid.querySelectorAll('[data-contrast-pair]');
    let visible = 0;
    tiles.forEach(tile => {
      const hit = !searchTerm
        || (tile.dataset.a || '').includes(searchTerm)
        || (tile.dataset.b || '').includes(searchTerm);
      tile.hidden = !hit;
      if (hit) visible++;
    });
    if (contrastEmpty)  contrastEmpty.hidden  = !(contrastRendered && tiles.length === 0);
    if (contrastNoRes)  contrastNoRes.hidden  = !(searchTerm && tiles.length > 0 && visible === 0);
  }

  function setMode(next) {
    if (next !== 'harmonic' && next !== 'contrast') return;
    mode = next;
    const harmonic = next === 'harmonic';

    modeBtns.forEach(b => {
      const on = b.dataset.mode === next;
      b.classList.toggle('is-active', on);
      b.setAttribute('aria-selected', on ? 'true' : 'false');
    });

    if (yarnTabs)     yarnTabs.hidden = !harmonic;
    if (grid)         grid.hidden = !harmonic;
    if (contrastWrap) contrastWrap.hidden = harmonic;

    if (harmonic) {
      if (contrastNoRes) contrastNoRes.hidden = true;
      applyFilters();
    } else {
      if (noResults) noResults.hidden = true; // harmonic-only message
      renderContrast();
    }
  }

  modeBtns.forEach(b => b.addEventListener('click', () => setMode(b.dataset.mode)));

  if (contrastGrid) {
    contrastGrid.addEventListener('click', e => {
      const atc = e.target.closest('[data-combo-atc]');
      if (atc) { addToCart(atc); return; }
      const media = e.target.closest('[data-contrast-media]');
      if (media) {
        const img = media.querySelector('img');
        if (img && window.BfComboLightbox) {
          window.BfComboLightbox.open(img.src, media.dataset.caption || '');
        }
      }
    });
  }

  // ── Add to cart ───────────────────────────────────────────────────────────

  function addToCart(btn) {
    const variantId = btn.dataset.comboAtc;
    if (!variantId) return;

    btn.setAttribute('data-adding', '');
    btn.disabled = true;
    btn.textContent = '…';

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: variantId, quantity: 1 })
    })
      .then(r => r.json())
      .then(() => fetch('/cart.js').then(r => r.json()))
      .then(cart => {
        document.dispatchEvent(new CustomEvent('boni:cart:added', { detail: cart }));
        btn.removeAttribute('data-adding');
        btn.setAttribute('data-added', '');
        btn.textContent = bfT('added', '✓ Hinzugefügt');
        setTimeout(() => {
          btn.removeAttribute('data-added');
          btn.disabled = false;
          btn.textContent = bfT('add_to_cart', 'In den Warenkorb');
        }, 2000);
      })
      .catch(() => {
        btn.removeAttribute('data-adding');
        btn.disabled = false;
        btn.textContent = bfT('add_to_cart', 'In den Warenkorb');
      });
  }

  // ── Init ──────────────────────────────────────────────────────────────────

  applyFilters();
})();
